import {
  getPreviewEvidenceCount,
  sixPillarsOverviewPreview,
  type OverviewPreviewItem,
  type OverviewPreviewSnapshot,
  type OverviewRoadmapItem,
  type OverviewSectionId,
  type RoadmapLaneKey,
} from "@/lib/six-pillars-preview";

/**
 * The Overview preview as one navigable document, mirroring the product's B6
 * overview: insights and roadmap steps carry ids, signal counts and links in
 * both directions (a step "addresses" insights; an insight lists its steps).
 * Insights and steps are numbered separately, in fixture order.
 */
export const OVERVIEW_SECTIONS: OverviewSectionId[] = [
  "issues",
  "opportunities",
  "strengths",
];

export const OVERVIEW_LANES: RoadmapLaneKey[] = [
  "today",
  "this_week",
  "this_month",
  "this_quarter",
];

export type OverviewInsight = OverviewPreviewItem & {
  kind: "insight";
  section: OverviewSectionId;
  signalCount: number;
  stepIds: string[];
};

export type OverviewStep = Omit<OverviewRoadmapItem, "addresses"> & {
  kind: "step";
  lane: RoadmapLaneKey;
  signalCount: number;
  insightIds: string[];
};

export type OverviewItem = OverviewInsight | OverviewStep;

export type OverviewDocument = Omit<
  OverviewPreviewSnapshot,
  "groups" | "roadmap"
> & {
  insights: Record<OverviewSectionId, OverviewInsight[]>;
  roadmap: Record<RoadmapLaneKey, OverviewStep[]>;
};

export function buildOverviewDocument(
  snapshot: OverviewPreviewSnapshot,
): OverviewDocument {
  const { groups, roadmap, ...rest } = snapshot;
  const groupItems = (section: OverviewSectionId) =>
    groups.find((group) => group.id === section)?.items ?? [];

  const resolveRef = (ref: string) => {
    const [section, position] = ref.split(":");
    const insight = groupItems(section as OverviewSectionId)[Number(position) - 1];

    if (!insight) {
      throw new Error(`Overview step addresses an unknown insight: ${ref}`);
    }

    return insight.id;
  };

  const steps = Object.fromEntries(
    OVERVIEW_LANES.map((lane) => [
      lane,
      roadmap[lane].map(({ addresses, ...step }) => ({
        ...step,
        kind: "step" as const,
        lane,
        signalCount: getPreviewEvidenceCount(step.signals),
        insightIds: addresses.map(resolveRef),
      })),
    ]),
  ) as Record<RoadmapLaneKey, OverviewStep[]>;

  const everyStep = OVERVIEW_LANES.flatMap((lane) => steps[lane]);
  const insights = Object.fromEntries(
    OVERVIEW_SECTIONS.map((section) => [
      section,
      groupItems(section).map((item) => ({
        ...item,
        kind: "insight" as const,
        section,
        signalCount: getPreviewEvidenceCount(item.signals),
        stepIds: everyStep
          .filter((step) => step.insightIds.includes(item.id))
          .map((step) => step.id),
      })),
    ]),
  ) as Record<OverviewSectionId, OverviewInsight[]>;

  return { ...rest, insights, roadmap: steps };
}

export const overviewDocument = buildOverviewDocument(sixPillarsOverviewPreview);

export function isStep(item: OverviewItem): item is OverviewStep {
  return item.kind === "step";
}

export function allInsights(document: OverviewDocument): OverviewInsight[] {
  return OVERVIEW_SECTIONS.flatMap((section) => document.insights[section]);
}

export function allSteps(document: OverviewDocument): OverviewStep[] {
  return OVERVIEW_LANES.flatMap((lane) => document.roadmap[lane]);
}

export function findItem(
  document: OverviewDocument,
  id: string,
): OverviewItem | null {
  return (
    [...allInsights(document), ...allSteps(document)].find(
      (item) => item.id === id,
    ) ?? null
  );
}

/** The window pages through every insight, or through every step. */
function siblingsOf(document: OverviewDocument, item: OverviewItem) {
  return isStep(item) ? allSteps(document) : allInsights(document);
}

export function positionOf(
  document: OverviewDocument,
  id: string,
): { position: number; total: number } | null {
  const item = findItem(document, id);

  if (!item) {
    return null;
  }

  const siblings = siblingsOf(document, item);

  return {
    position: siblings.findIndex((sibling) => sibling.id === id) + 1,
    total: siblings.length,
  };
}

export function neighbourId(
  document: OverviewDocument,
  id: string,
  delta: 1 | -1,
): string | null {
  const item = findItem(document, id);

  if (!item) {
    return null;
  }

  const siblings = siblingsOf(document, item);
  const index = siblings.findIndex((sibling) => sibling.id === id);

  return siblings[index + delta]?.id ?? null;
}

/** ArrowDown pages forward and ArrowUp back; every other key leaves the window alone. */
export function windowKeyDelta(key: string): -1 | 0 | 1 {
  if (key === "ArrowDown") {
    return 1;
  }

  if (key === "ArrowUp") {
    return -1;
  }

  return 0;
}

export function isViewerItem(document: OverviewDocument, item: OverviewItem) {
  return item.suggestedOwner === document.viewer;
}

export function stepsOwnedByViewer(document: OverviewDocument): OverviewStep[] {
  return allSteps(document).filter((step) => isViewerItem(document, step));
}

export type OverviewMapDot = {
  id: string;
  title: string;
  position: number;
  /** The step the window shows. */
  current: boolean;
  /** In an insight window: this step addresses the insight. */
  linked: boolean;
};

/**
 * "Where it sits in the plan": every step by horizon. A step window marks the
 * step and its horizon; an insight window marks the steps that address it.
 */
export function laneDotMap(document: OverviewDocument, id: string) {
  const item = findItem(document, id);
  const steps = allSteps(document);
  const linked = (step: OverviewStep) =>
    item !== null && !isStep(item) && item.stepIds.includes(step.id);

  return OVERVIEW_LANES.map((lane) => ({
    lane,
    current:
      item !== null &&
      (isStep(item) ? item.lane === lane : document.roadmap[lane].some(linked)),
    dots: document.roadmap[lane].map(
      (step): OverviewMapDot => ({
        id: step.id,
        title: step.title,
        position: steps.indexOf(step) + 1,
        current: step.id === id,
        linked: linked(step),
      }),
    ),
  }));
}

/** A step lists the insights it addresses; an insight lists the steps that address it. */
export function linkedItems(
  document: OverviewDocument,
  id: string,
): OverviewItem[] {
  const item = findItem(document, id);
  const ids = item ? (isStep(item) ? item.insightIds : item.stepIds) : [];

  return ids
    .map((linkedId) => findItem(document, linkedId))
    .filter((linked): linked is OverviewItem => linked !== null);
}

/**
 * The insight folder: three type tiles, at most one open. In a wide deck the
 * open tile becomes the front tab of a panel below the tiles and the others
 * stand behind it as back tabs; in a narrow deck cards open under their tile.
 */
export type FolderTab = "front" | "back" | "rest";

export function folderTab(
  open: OverviewSectionId | null,
  section: OverviewSectionId,
  narrow: boolean,
): FolderTab {
  if (open === section) {
    return "front";
  }

  return open !== null && !narrow ? "back" : "rest";
}

/** A tile opens its type, switches from another type, or closes its own. */
export function toggleFolder(
  open: OverviewSectionId | null,
  picked: OverviewSectionId,
): OverviewSectionId | null {
  return open === picked ? null : picked;
}
